/**
 * REST API Client
 *
 * @package DesignSchool\Forms
 */

declare var dsFormsConfig: any;

export class ApiClient {
    private baseUrl: string;
    private nonce: string;

    constructor() {
        this.baseUrl = dsFormsConfig.restUrl || '/wp-json/designschool/v1/';
        this.nonce = dsFormsConfig.nonce || '';
    }

    /**
     * Get list of forms
     */
    public async getForms(): Promise<any> {
        return this.request('forms');
    }

    /**
     * Get single form HTML
     */
    public async getForm(formId: string, postId?: number): Promise<any> {
        const params = postId ? `?post_id=${postId}` : '';
        return this.request(`forms/${formId}${params}`);
    }

    /**
     * Submit form
     */
    public async submitForm(formId: string, data: FormData): Promise<any> {
        const body: Record<string, any> = {};

        data.forEach((value, key) => {
            body[key] = value;
        });

        return this.request(`forms/${formId}/submit`, {
            method: 'POST',
            body: JSON.stringify(body),
        });
    }

    /**
     * Check form availability
     */
    public async checkAvailability(formId: string): Promise<any> {
        return this.request(`forms/${formId}/availability`);
    }

    /**
     * Get programs for category
     */
    public async getPrograms(type: string, categorySlug: string): Promise<any[]> {
        const response = await this.request(`programs/${type}/${categorySlug}`);
        return response || [];
    }

    /**
     * Get categories for type
     */
    public async getCategories(type: string): Promise<any[]> {
        const response = await this.request(`programs/${type}/categories`);
        return response || [];
    }

    /**
     * Get certificates
     */
    public async getCertificates(city?: string): Promise<any[]> {
        const params = city ? `?city=${city}` : '';
        const response = await this.request(`certificates${params}`);
        return response || [];
    }

    /**
     * Get open lessons
     */
    public async getOpenLessons(city?: string): Promise<any[]> {
        const params = city ? `?city=${city}` : '';
        const response = await this.request(`open-lessons${params}`);
        return response || [];
    }

    /**
     * Generic request method
     */
    private async request(endpoint: string, options: RequestInit = {}): Promise<any> {
        const url = this.baseUrl + endpoint;

        // Determine if this is a GET request (read-only)
        const isGetRequest = !options.method || options.method === 'GET';

        const defaultOptions: RequestInit = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        // Only add nonce for POST/PUT/DELETE requests (write operations)
        // GET requests are public and don't need nonce
        if (!isGetRequest && this.nonce) {
            defaultOptions.headers = {
                ...defaultOptions.headers,
                'X-WP-Nonce': this.nonce,
            };
        }

        // Debug logging
        console.log('[ApiClient] Request:', {
            url,
            method: options.method || 'GET',
            isGetRequest,
            hasNonce: !isGetRequest && !!this.nonce,
            headers: defaultOptions.headers
        });

        const response = await fetch(url, { ...defaultOptions, ...options });

        if (!response.ok) {
            let errorData: any;

            try {
                errorData = await response.json();
            } catch (e) {
                // If response is not JSON, create error object
                errorData = {
                    message: `HTTP ${response.status}: ${response.statusText}`,
                    code: 'http_error',
                    status: response.status
                };
            }

            // Create error object with all details
            const error: any = new Error(errorData.message || 'API Error');
            error.code = errorData.code || 'api_error';
            error.status = response.status;
            error.message = errorData.message || error.message;
            
            // Извлекаем ошибки из разных возможных мест в ответе
            error.errors = errorData.errors || errorData.data?.errors || null;
            
            // Если ошибки в формате массива (WP_Error format)
            if (errorData.data && errorData.data.errors && typeof errorData.data.errors === 'object') {
                // Преобразуем массив ошибок в объект, если нужно
                const errorsObj: any = {};
                Object.keys(errorData.data.errors).forEach(key => {
                    const fieldErrors = errorData.data.errors[key];
                    if (Array.isArray(fieldErrors)) {
                        errorsObj[key] = fieldErrors[0]; // Берем первую ошибку
                    } else {
                        errorsObj[key] = fieldErrors;
                    }
                });
                error.errors = errorsObj;
            }
            
            error.data = errorData.data || errorData;

            throw error;
        }

        return response.json();
    }
}

// Export singleton
export const apiClient = new ApiClient();

